import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Header } from './Header';

/**
 * Catches render-time errors in its subtree so a single malformed record
 * cannot blank the entire page. Surfaces a friendly fallback UI with the
 * error message and a way to recover, and logs the full error + component
 * stack to the browser console (and any window-level error reporter) so we
 * can still debug.
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('[ErrorBoundary] Render failure:', error, info);
        if (typeof window !== 'undefined' && typeof window.onerror === 'function') {
            try { window.onerror(error?.message, null, null, null, error); } catch (_e) { /* noop */ }
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const { withHeader = true, title = 'Something went wrong', description } = this.props;
        const errorMessage = this.state.error?.message || 'Unknown error';

        return (
            <div className="min-h-screen bg-background" data-testid="error-boundary-fallback">
                {withHeader && <Header />}
                <main className="container mx-auto px-4 py-12 max-w-2xl">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <h2 className="text-lg font-semibold text-amber-900">{title}</h2>
                                <p className="text-sm text-amber-800 mt-1">
                                    {description || 'This page could not be rendered because of an unexpected data condition. The error has been logged.'}
                                </p>
                                <pre className="mt-3 text-xs text-amber-900 bg-amber-100 rounded px-3 py-2 overflow-x-auto whitespace-pre-wrap break-words">
                                    {errorMessage}
                                </pre>
                                <div className="mt-4 flex gap-2">
                                    <Button size="sm" variant="outline" onClick={this.handleReset} data-testid="error-boundary-retry-btn">
                                        Try again
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => window.history.back()} data-testid="error-boundary-back-btn">
                                        Go back
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }
}

export default ErrorBoundary;
